/* eslint-disable max-lines */
import React from "react";
import styled, { css } from "styled-components";
import { Flex } from "grid-styled";
import { ellipsis } from "polished";
import {
  MapLegendValues,
  MapLegendExpandButton,
  MapLegendItem,
  MapLegendDescriptionContainer,
  MapLegendContainer,
  SizeMinimizedLegendDown,
} from "@evergis/react";
import { Chip, Description, Icon, IconButton, Popup, transition } from "@evergis/ui";

import { DraggableList } from "../../components/DraggableList/DraggableList";
import { PROJECT_PANEL_HEADER_HEIGHT } from "../ProjectPanel/constants";

type HalfVisibleProps = { halfVisible?: boolean };
type ErrorInfoProps = { withError?: boolean };
type DescriptionProps = ErrorInfoProps &
  HalfVisibleProps & {
    title?: string;
  };

const halfVisibleMixin = css<HalfVisibleProps>`
  opacity: ${({ halfVisible }) => (halfVisible ? "0.5" : "1")};
`;

const errorInfoMixin = css<ErrorInfoProps>`
  ${({ withError }) =>
    withError &&
    `
    color: #f5a623;
  `};
`;

export const DnDAnchor = styled.div<{ visible?: boolean }>`
  position: absolute;
  left: -0.125rem;
  height: 100%;
  width: 1rem;
  display: flex;
  align-items: center;
  transition: opacity ${transition.hover};
  opacity: ${({ visible }) => +visible};
`;

type ListItemProps = {
  zIndex?: number;
  isDragging?: boolean;
  disableShadow?: boolean;
  shared?: boolean;
};

const DraggingListItem = css`
  background-color: ${({ theme: { palette } }) => palette.background};
  box-shadow: ${({ theme: { shadows } }) => shadows.raised};
`;

export const ListItem = styled.div<ListItemProps & { shared?: boolean; noVisibilityClick?: boolean }>`
  z-index: ${({ zIndex }) => zIndex || 0};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.xSmall};
  overflow: hidden;
  transition: ${transition.press};
  transition-property: box-shadow, background-color;
  cursor: ${({ shared, noVisibilityClick }) => (shared || noVisibilityClick ? "default" : "pointer")};
  position: relative;
  outline: 0;

  &:last-child {
    margin-bottom: 1rem;
  }

  &:hover > ${DnDAnchor} {
    opacity: 1;
  }

  ${({ isDragging }) => isDragging && DraggingListItem};
`;

export const IconContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  margin-right: 0.375rem;

  label {
    margin-right: -0.5rem;
  }
`;

export const Section = styled(Flex)`
  flex-grow: 1;
  flex-direction: column;
  width: 50%;
`;

export const Content = styled(Flex)`
  align-items: center;
  padding: 0.5rem 0;
  width: 100%;

  span[kind="expand"] {
    margin-right: 0.75rem;
  }
`;

export const LegendToggler = styled(IconButton)<{ toggled?: boolean; color?: string }>`
  margin: 0;
  width: 100%;
  height: auto;
  cursor: pointer;
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;

  &:hover,
  &:active {
    ${Icon}, ${Icon} + span {
      color: ${({ theme: { palette }, color }) => color || palette.primary};
      transform: scale(1);
    }
  }

  ${Icon} + span {
    line-height: 0;
    font: ${({ theme: { fonts } }) => fonts.small};
    color: ${({ theme: { palette }, color }) => color || palette.primary};
    margin: 0 0.25rem 0 0;
  }

  ${Icon}, ${Icon} + span {
    align-items: center;
    color: ${({ theme: { palette }, color }) => color || palette.primary};
    display: flex;
    text-align: left;

    &:after {
      margin-left: 2px;
      position: static;
      transform: ${({ toggled }) => `rotate(${toggled ? 180 : 0}deg)`};
      transition: transform 289ms ${transition.upDown};
    }
  }
`;
LegendToggler.defaultProps = { kind: "expand" };

export const Paragraph = styled(Flex)`
  justify-content: center;
  flex-direction: column;

  ${LegendToggler} span[kind] {
    opacity: 0;
    transition: opacity ${transition.hover};
  }

  :hover ${LegendToggler} span[kind] {
    opacity: 1;
  }
`;

export const Alias = styled(Description)<DescriptionProps>`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme: { palette } }) => palette.textPrimary};

  position: relative;
  max-height: 3rem;
  overflow: hidden;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  word-break: break-word;

  ${halfVisibleMixin};
  ${errorInfoMixin};
`;

export const Badge = styled.div`
  margin-right: 0.5rem;
  padding: 0.125rem 0.375rem;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  background-color: ${({ theme: { palette } }) => palette.elementDark};
  font-size: 0.625rem;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const StyledMapLegend = styled.div`
  padding: 0.25rem 1rem 0 3.25rem;

  ${MapLegendContainer} {
    cursor: default;
    color: ${({ theme }) => theme.palette.textPrimary};
  }

  ${MapLegendValues} {
    color: ${({ theme }) => theme.palette.textSecondary};
  }

  ${MapLegendExpandButton} {
    color: ${({ theme }) => theme.palette.primary};
  }

  ${MapLegendItem} {
    color: ${({ theme }) => theme.palette.textSecondary};

    :after {
      border-color: ${({ theme }) => theme.palette.icon};
      opacity: 0.5;
    }
  }

  ${SizeMinimizedLegendDown} {
    color: ${({ theme }) => theme.palette.textSecondary};
  }

  ${MapLegendDescriptionContainer} {
    color: ${({ theme }) => theme.palette.textSecondary};
  }
`;

export const ServiceDescr = styled(Description)<DescriptionProps>`
  ${ellipsis() as string};
  margin: 0;
  word-break: break-word;

  ${errorInfoMixin};
`;

export const ServicePath = styled(Description)<DescriptionProps>`
  ${ellipsis() as string};
  margin: 0 1rem 0 0;
  font-size: 0.625rem;
`;

export const LayerIconContainer = styled.div<
  { isActive?: boolean } & HalfVisibleProps & React.HTMLAttributes<HTMLDivElement>
>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
  transition: background-color ${transition.hover};
  height: 2.25rem;
  width: 2.25rem;
  box-sizing: border-box;
  pointer-events: ${({ halfVisible }) => halfVisible && "none"};
  cursor: ${({ isActive }) => isActive && "pointer"};

  &:hover {
    background-color: ${({ onClick, isActive, theme: { palette } }) => onClick && isActive && palette.elementDark};
  }

  svg {
    max-width: 2rem;
    max-height: 2rem;
  }

  label {
    margin-right: -0.5rem;
  }

  ${halfVisibleMixin};
`;

const folderMixin = css<{ $isExpanded?: boolean; $color?: string }>`
  ${LayerIconContainer} ${Icon} {
    color: ${({ theme: { palette } }) => palette.icon};
  }

  ${Content} span[kind="expand"] {
    margin-left: 0.5rem;
    opacity: 0;
    transform: rotate(${({ $isExpanded }) => ($isExpanded ? 180 : 0)}deg);
    transition: transform 300ms cubic-bezier(0.15, 1.35, 0.7, 1.5), opacity ${transition.hover};
  }

  ${Alias} {
    font-family: "Roboto Medium", sans-serif;
  }

  ${LayerIconContainer} span[kind]:after {
    position: relative;
    top: -0.125rem;
    font-size: 1.25rem;
    color: ${({ theme: { palette }, $color }) => $color || palette.primary};
  }
`;

const expandingMixin = css`
  animation-duration: 0.25s;
  animation-name: fadeIn;
  animation-timing-function: linear;

  @keyframes fadeIn {
    from {
      margin-top: -2rem;
      opacity: 0;
    }

    to {
      margin-top: 0;
      opacity: 1;
    }
  }
`;

export const Main = styled(Flex)<{
  $color?: string;
  $isFolder?: boolean;
  $isExpanding?: boolean;
  $isExpanded?: boolean;
  $isVisible?: boolean;
  $noControl?: boolean;
  $noVisibilityClick?: boolean;
}>`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  width: 100%;
  padding: 0 0 0 0.625rem;
  box-sizing: border-box;
  transition: opacity ${transition.hover};

  .feature-create-button {
    opacity: 0;
    transition: opacity ${transition.hover};
  }

  :hover {
    .feature-create-button {
      opacity: 1;
    }

    ${Content} span[kind="expand"] {
      opacity: 1;
    }
  }

  ${({ $isExpanding }) => $isExpanding && expandingMixin};
  ${({ $isFolder }) => $isFolder && folderMixin};

  ${({ $noControl, $isVisible, $noVisibilityClick }) =>
    $noControl &&
    !$noVisibilityClick &&
    css`
      cursor: pointer;

      :not(:hover) {
        opacity: ${$isVisible ? 1 : 0.5};
      }
    `};
`;

export const Control = styled(({ sharedVersion, ...rest }) => <Flex {...rest} />)<{ sharedVersion?: boolean }>`
  align-items: center;
  margin-left: auto;
  margin-right: ${({ sharedVersion }) => (!sharedVersion ? 0 : "0.5rem")};
`;

export const Title = styled(({ loading, ...rest }) => <Flex {...rest} />)<{ loading: boolean }>`
  font: ${({ theme: { fonts } }) => fonts.subtitle};
  background: ${({ theme: { palette }, loading }) => loading && palette.elementDark};
  height: ${({ loading }) => loading && "1rem"};
  border-radius: ${({ theme: { borderRadius }, loading }) => (loading ? borderRadius.medium : "0")};
  color: ${({ loading, theme: { palette } }) => (loading ? "transparent" : palette.textPrimary)};
`;

export const LayerList = styled(DraggableList)`
  flex: 1;
  flex-direction: column;
  flex-wrap: nowrap;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 0.125rem 0 0.75rem;
  scrollbar-gutter: stable;
`;

export const LayerGroupList = styled(Flex)<{ $noScroll?: boolean }>`
  flex-direction: column;
  height: 100%;
  flex-wrap: nowrap;
  overflow-x: ${({ $noScroll }) => ($noScroll ? "visible" : "hidden")};
  overflow-y: ${({ $noScroll }) => ($noScroll ? "visible" : "auto")};
  padding: 0 0.125rem 0 0.75rem;
  scrollbar-gutter: stable;

  > * {
    flex-grow: 1;
  }
`;

export const LayerListContainer = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  flex-wrap: nowrap;
  height: calc(100vh - ${PROJECT_PANEL_HEADER_HEIGHT} - 1.5rem);
`;

export const SearchMenu = styled(Flex)`
  position: absolute;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
`;

export const SearchButton = styled(IconButton)`
  margin: 0 -0.5rem;
`;

export const ExpandButton = styled(IconButton).attrs({ kind: "expand" })``;

export const SelectionButton = styled(IconButton).attrs({ kind: "search_selection" })`
  margin-left: -0.125rem;
  margin-right: 0.125rem;
`;

export const DeleteButton = styled(IconButton).attrs({ kind: "delete" })`
  margin: 0 -0.25rem 0 -0.165rem;
`;

export const Header = styled(Flex)<{ $isFixedExpandButton?: boolean }>`
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  height: 4.625rem;
  margin: -1.5rem 0 1rem;
  padding: 1.5rem 1.125rem 0 3.5rem;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme: { palette } }) => palette.elementDark};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.large};

  ${SearchMenu} {
    left: ${({ $isFixedExpandButton }) => ($isFixedExpandButton ? "1rem" : "2rem")};
    transition: left ${transition.hover};
  }

  ${ExpandButton}, ${SelectionButton} {
    opacity: ${({ $isFixedExpandButton }) => ($isFixedExpandButton ? 1 : 0)};
    transition: opacity ${transition.hover};
  }

  input {
    background-color: transparent;
    border: 0;
  }

  :hover {
    ${SearchMenu} {
      left: 1rem;
    }

    ${ExpandButton}, ${SelectionButton} {
      opacity: 1;
    }
  }
`;

export const PopupWithCustomBlank = styled(Popup)`
  min-width: 14.75rem;
  max-height: none;
  padding: 0.64rem 0;
  overflow-y: hidden;
`;

export const OptionText = styled.div`
  min-height: 1.25em;
  display: flex;
  align-items: center;
`;

export const OptionChip = styled(Chip)`
  margin-left: 0.5rem;
  padding: 2px 9px;
  min-height: 1.25em;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.small};
  min-width: 1.25em;
  box-sizing: border-box;

  span {
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.08px;
    font-size: 0.65rem;
  }
`;

export const LegendContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  width: 100%;
`;
